/**
 * @nova/sdk — Nova SDK for customer applications.
 *
 * Hybrid model: SDK sends all telemetry (errors, engagement, heartbeat,
 * diagnostics) to Nova's API Gateway via HMAC-signed HTTP POST.
 * Nova backend writes to the customer's CloudWatch via cross-account IAM.
 *
 * Zero external runtime dependencies.
 */

import type {
  NovaConfig,
  CapturedError,
  LogEvent,
  BugReport,
} from './types.js';
import { ConsoleBuffer } from './diagnostics/console-buffer.js';
import { ErrorCapture } from './diagnostics/error-capture.js';
import { NetworkCapture } from './diagnostics/network-capture.js';
import { RouteCapture } from './diagnostics/route-capture.js';
import { EngagementTracker } from './engagement/tracker.js';
import { HttpTransport } from './writer/http-transport.js';
import { EventBatcher } from './writer/batcher.js';
import { scrubPII, scrubObject } from './pii/index.js';
import { NovaEventEmitter } from './events/emitter.js';

const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

export class Nova {
  private static instance: Nova | null = null;
  private static emitter = new NovaEventEmitter();

  private appId: string;
  private externalUserId: string | null = null;
  private widget: unknown = null; // NovaChatWidget — typed as unknown to avoid circular dep
  private batcher: EventBatcher;
  private consoleBuffer: ConsoleBuffer;
  private errorCapture: ErrorCapture;
  private networkCapture: NetworkCapture;
  private routeCapture: RouteCapture;
  private engagementTracker: EngagementTracker;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private sessionStart: number;

  private constructor(config: NovaConfig) {
    this.appId = config.appId;
    this.externalUserId = config.chat?.externalUserId ?? null;
    this.sessionStart = Date.now();

    // Initialize HTTP transport and batcher
    const transport = new HttpTransport({
      apiUrl: config.apiUrl,
      publishableKey: config.publishableKey,
      appId: config.appId,
    });
    this.batcher = new EventBatcher(transport);

    // Initialize diagnostics
    this.consoleBuffer = new ConsoleBuffer();
    this.networkCapture = new NetworkCapture();
    this.routeCapture = new RouteCapture();

    this.errorCapture = new ErrorCapture(
      (error: CapturedError) => this.onError(error),
      {
        getConsoleEntries: () => this.consoleBuffer.getEntries(),
        getRouteEntries: () => this.routeCapture.getEntries(),
        getNetworkFailures: () => this.networkCapture.getRecent(10),
      },
    );

    // Initialize engagement tracker
    this.engagementTracker = new EngagementTracker(
      (event: LogEvent) => this.batcher.enqueue(event),
    );

    // Install all interceptors
    this.consoleBuffer.install();
    this.networkCapture.install();
    this.routeCapture.install();
    this.errorCapture.install();

    // Start batcher and heartbeat
    this.batcher.start();
    this.startHeartbeat();

    // Lazy-load chat widget when configured
    if (config.chat) {
      const chatConfig = config.chat;
      import('./widget/index.js').then(({ initWidget }) => {
        this.widget = initWidget({
          ...chatConfig,
          theme: chatConfig.theme ? {
            brandColor: chatConfig.theme.brandColor,
            backgroundColor: chatConfig.theme.backgroundColor,
            textColor: chatConfig.theme.textColor,
            fontFamily: chatConfig.theme.fontFamily,
            borderRadius: chatConfig.theme.borderRadius,
          } : undefined,
          appId: config.appId,
          apiUrl: config.apiUrl,
          apiKey: config.publishableKey,
          apiSecret: config.publishableKey,
          emitter: Nova.emitter as unknown as { emit: (event: string, data: unknown) => void },
        });
      });
    }
  }

  /**
   * Initialize the Nova SDK singleton.
   *
   * @param config.appId - Unique application identifier from Nova
   * @param config.apiUrl - Nova SDK API URL
   * @param config.publishableKey - Publishable key (pk_nova_ prefix, safe to embed in code)
   */
  static init(config: NovaConfig): Nova {
    if (Nova.instance) {
      return Nova.instance;
    }

    if (!config.appId || !config.apiUrl || !config.publishableKey) {
      const missing = [
        !config.appId && 'appId',
        !config.apiUrl && 'apiUrl',
        !config.publishableKey && 'publishableKey',
      ].filter(Boolean).join(', ');
      console.warn(
        `[Nova] SDK not initialized — missing: ${missing}. ` +
        'Set the NOVA_APP_ID, NOVA_API_URL, and NOVA_PUBLISHABLE_KEY environment variables.',
      );
      return Nova.instance as unknown as Nova;
    }

    Nova.instance = new Nova(config);
    return Nova.instance;
  }

  /**
   * Set the external user ID for the current session.
   * Used by CDN bootstrap to associate the session with a known user
   * after the SDK has been initialized.
   *
   * @param externalUserId - External user ID to associate with this session
   */
  static identify(externalUserId: string): void {
    if (!Nova.instance) {
      console.warn('[Nova] identify() called before init()');
      return;
    }
    Nova.instance.externalUserId = externalUserId;

    // Forward to the chat widget if it supports user updates
    const widget = Nova.instance.widget as Record<string, unknown> | null;
    if (widget && typeof widget.updateUser === 'function') {
      (widget as { updateUser: (id: string) => void }).updateUser(
        Nova.instance.externalUserId,
      );
    }
  }

  /**
   * Track an engagement event.
   * Properties are PII-scrubbed before being written.
   *
   * @param event - Event name (e.g., 'page_view', 'button_click')
   * @param properties - Additional event properties
   */
  static track(event: string, properties?: Record<string, unknown>): void {
    const instance = Nova.getInstance();
    if (!instance) return;
    instance.engagementTracker.track(event, properties);
  }

  /**
   * Submit a manual bug report.
   * Includes console context, route history, and recent network failures.
   *
   * @param description - User-provided bug description
   * @param metadata - Additional context
   */
  static report(description: string, metadata?: Record<string, unknown>): void {
    const instance = Nova.getInstance();
    if (!instance) return;

    const report: BugReport = {
      description: scrubPII(description),
      metadata: metadata ? scrubObject(metadata) as Record<string, unknown> : {},
      timestamp: new Date().toISOString(),
      consoleContext: instance.consoleBuffer.getEntries().map(scrubPII),
      routeHistory: instance.routeCapture.getEntries(),
      recentNetworkFailures: instance.networkCapture.getRecent(10),
    };

    instance.batcher.enqueue({
      stream: 'chatbot',
      data: report,
    });
  }

  /**
   * Manually flush all pending events to CloudWatch.
   * Useful before page navigation or unload.
   */
  static async flush(): Promise<void> {
    if (!Nova.instance) return;
    await Nova.instance.batcher.flush();
  }

  /** Subscribe to a Nova SDK event. */
  static on = Nova.emitter.on.bind(Nova.emitter);

  /** Unsubscribe from a Nova SDK event. */
  static off = Nova.emitter.off.bind(Nova.emitter);

  /** Programmatically open the chat widget. */
  static openChat(): void {
    const instance = Nova.instance;
    if (
      instance?.widget &&
      typeof (instance.widget as Record<string, unknown>).open === 'function'
    ) {
      (instance.widget as { open: () => void }).open();
    }
  }

  /** Programmatically close the chat widget. */
  static closeChat(): void {
    const instance = Nova.instance;
    if (
      instance?.widget &&
      typeof (instance.widget as Record<string, unknown>).close === 'function'
    ) {
      (instance.widget as { close: () => void }).close();
    }
  }

  /**
   * Shut down the SDK.
   * Flushes remaining events, stops timers, uninstalls interceptors.
   */
  static async destroy(): Promise<void> {
    if (!Nova.instance) return;

    const instance = Nova.instance;

    // Stop heartbeat
    if (instance.heartbeatTimer) {
      clearInterval(instance.heartbeatTimer);
      instance.heartbeatTimer = null;
    }

    // Uninstall interceptors
    instance.errorCapture.uninstall();
    instance.networkCapture.uninstall();
    instance.routeCapture.uninstall();
    instance.consoleBuffer.uninstall();

    // Destroy chat widget
    if (
      instance.widget &&
      typeof (instance.widget as Record<string, unknown>).destroy === 'function'
    ) {
      (instance.widget as { destroy: () => void }).destroy();
    }
    instance.widget = null;

    // Flush and stop batcher
    await instance.batcher.shutdown();

    Nova.emitter.removeAllListeners();
    Nova.instance = null;
  }

  /** Get the singleton instance, or null if not initialized */
  private static getInstance(): Nova | null {
    return Nova.instance;
  }

  /** Handle a captured error — enqueue for writing */
  private onError(error: CapturedError): void {
    this.batcher.enqueue({
      stream: 'errors',
      data: error,
    });
  }

  /** Start the 5-minute heartbeat */
  private startHeartbeat(): void {
    this.heartbeatTimer = setInterval(() => {
      this.batcher.enqueue({
        stream: 'engagement',
        data: {
          event: 'heartbeat',
          properties: {
            appId: this.appId,
            sessionDurationMs: Date.now() - this.sessionStart,
          },
          timestamp: new Date().toISOString(),
        },
      });
    }, HEARTBEAT_INTERVAL_MS);
  }
}

// Re-export types for consumers
export type {
  NovaConfig,
  NovaWidgetTheme,
  CapturedError,
  NetworkFailure,
  RouteEntry,
  EngagementEvent,
  BugReport,
  LogEvent,
  FlushResult,
} from './types.js';

// Re-export PII scrubbing for advanced usage
export { scrubPII, scrubObject } from './pii/index.js';

// Re-export event emitter for advanced usage
export { NovaEventEmitter } from './events/emitter.js';
export type { EventMap } from './events/emitter.js';
